import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { goalFragment } from '../../client/fragments/goal';
import { GetGoalsQuery, GetSingleGoalQuery, GetSingleGoalQueryVariables, GoalFieldsFragment } from '../../client/generated/graphql';
import { getGoalsQuery } from '../../client/queries/goals';
import { getSingleGoalQuery } from '../../client/queries/singleGoal';
import { ListGoal } from '../../client/types';
import { useCachedCategoryWithFallback } from './category';

export const useCachedGoal = (goalId?: string | null) => {
    const client = useApolloClient();

    if (!goalId) return null;

    try {
        return client.cache.readFragment<GoalFieldsFragment>({
            id: client.cache.identify({ __typename: 'Goal', id: goalId }),
            fragment: goalFragment,
            fragmentName: 'GoalFields',
        });
    } catch {
        return null;
    }
};

// Get all Goal objects from cache regardless of which query fetched them
export const useCachedGoals = (): ListGoal[] => {
    const client = useApolloClient();

    return useMemo(() => {
        const cacheData = client.cache.extract();
        const allGoals = Object.entries(cacheData)
            .filter(([key, value]: [string, any]) => key.startsWith('Goal:') || value?.__typename === 'Goal')
            .map(([_key, value]) => value as ListGoal);

        return allGoals;
    }, [client.cache]);
};

export const useCachedGoalsWithFallback = (): {
    goals: ListGoal[];
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const cachedGoals = useCachedGoals();

    // If nothing in cache, fetch all goals
    const { data, loading, error } = useQuery<GetGoalsQuery>(getGoalsQuery, {
        skip: cachedGoals.length > 0,
        fetchPolicy: 'cache-first',
    });

    const goals = cachedGoals.length > 0 ? cachedGoals : data?.goals ?? [];

    return {
        goals,
        loading: loading && cachedGoals.length === 0,
        error,
    };
};

export const useCachedGoalWithFallback = (
    goalId?: string | null,
): {
    goal: GoalFieldsFragment | null;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const client = useApolloClient();

    const cachedGoal = goalId
        ? client.cache.readFragment<GoalFieldsFragment>({
              id: client.cache.identify({ __typename: 'Goal', id: goalId }),
              fragment: goalFragment,
              fragmentName: 'GoalFields',
          })
        : null;

    const {
        data: networkData,
        loading,
        error,
    } = useQuery<GetSingleGoalQuery, GetSingleGoalQueryVariables>(getSingleGoalQuery, {
        variables: { id: goalId! },
        skip: !goalId || !!cachedGoal,
        fetchPolicy: 'cache-first',
    });

    if (!goalId) {
        return { goal: null, loading: false, error: undefined };
    }

    const goal = cachedGoal ?? networkData?.goal ?? null;

    return {
        goal,
        loading: loading && !cachedGoal,
        error,
    };
};

export const useCachedGoalCategoryWithFallback = (
    goal: GetGoalsQuery['goals'][0] | GetSingleGoalQuery['goal'] | GoalFieldsFragment | null | undefined,
): {
    category: any | null;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const embeddedCategory = useMemo(() => {
        if (!goal) return null;
        if ('category' in goal && goal.category) {
            return goal.category;
        }
        return null;
    }, [goal]);

    const { category: fetchedCategory, loading, error } = useCachedCategoryWithFallback(embeddedCategory ? null : goal?.categoryId);

    return {
        category: embeddedCategory ?? fetchedCategory,
        loading,
        error,
    };
};
